import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useCreate, useCreateSuggestionContext, useNotify } from 'react-admin';
import { getErrorMessage } from '../utils/helperUtils.ts';

type QuickCreateDialogProps<T> = {
  resource: string;
  title: string;
  // Seeded from whatever the user typed into the picker.
  defaults: (typedText: string) => T;
  // Return a message to block the save, or null to allow it.
  validate?: (draft: T) => string | null;
  children: (draft: T, setDraft: (next: T) => void) => React.ReactNode;
};

// Lets a reference picker create the missing record without leaving the form.
// The dialog and save flow are the same everywhere; only the fields differ, so
// callers supply those and nothing else.
export function QuickCreateDialog<T extends Record<string, unknown>>({
  resource,
  title,
  defaults,
  validate,
  children
}: QuickCreateDialogProps<T>) {
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [draft, setDraft] = useState<T>(() => defaults(filter ?? ''));

  const handleSave = () => {
    const problem = validate?.(draft);
    if (problem) {
      notify(problem, { type: 'warning' });
      return;
    }

    create(
      resource,
      { data: draft },
      {
        onSuccess: (created) => {
          // Handing the record back is what selects it in the picker.
          onCreate(created);
        },
        onError: (error) =>
          notify(getErrorMessage(error), { type: 'error' })
      }
    );
  };

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {children(draft, setDraft)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isLoading}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
